import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowRight, Lightbulb, ShieldCheck, Shield, Boxes, Route, Building2, Trees, Sparkles, Anchor, Wrench, Fuel, Users, Award, Globe, Waves, Landmark, Ship, Sailboat, Factory, Zap, Headphones, Leaf, LifeBuoy, Flame, Container, Layers, Utensils, Link2 } from "lucide-react";

const HERO_IMG = "https://customer-assets-lxgj4vgw.emergentagent.net/job_mia-waterfront/artifacts/9wa5vq0x_file_000000005a5c8210b6978993d14f76f4.png";

// Custom marine navigation beacon / lighthouse icon
const BeaconIcon = ({ className, strokeWidth = 1.5 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    {/* light rays */}
    <path d="M4.2 6.5 L1.5 5.5" opacity="0.6" />
    <path d="M4.2 8.5 L1.5 9.5" opacity="0.6" />
    <path d="M19.8 6.5 L22.5 5.5" opacity="0.6" />
    <path d="M19.8 8.5 L22.5 9.5" opacity="0.6" />
    {/* lamp */}
    <rect x="9.5" y="4" width="5" height="4.5" rx="0.6" />
    <path d="M11 4 V2.5 M13 4 V2.5" />
    <circle cx="12" cy="6.25" r="0.9" fill="currentColor" stroke="none" />
    {/* gallery / walkway */}
    <path d="M8.5 9 H15.5" />
    {/* tower */}
    <path d="M9 9 L8 20 H16 L15 9" />
    {/* stripes */}
    <path d="M8.6 13 H15.4" opacity="0.7" />
    <path d="M8.35 16.5 H15.65" opacity="0.7" />
    {/* base / water line */}
    <path d="M6 20 H18" />
    <path d="M4 22 Q6 21 8 22 T12 22 T16 22 T20 22" opacity="0.7" />
  </svg>
);

// Custom marine fuel storage tank + dispenser icon
const FuelTankIcon = ({ className, strokeWidth = 1.5 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    {/* storage tank body (vertical cylinder) */}
    <path d="M4.5 6 Q4.5 3.5 7 3.5 H11 Q13.5 3.5 13.5 6 V18 H4.5 Z" />
    {/* tank top band */}
    <path d="M4.5 7 H13.5" opacity="0.6" />
    {/* pressure gauge */}
    <circle cx="9" cy="10.5" r="1.1" />
    {/* fuel type label */}
    <path d="M6.5 13.5 H11.5" opacity="0.6" />
    {/* pipe from tank to dispenser */}
    <path d="M13.5 10 H16 V13" />
    {/* dispenser body */}
    <rect x="16" y="13" width="4" height="5" rx="0.4" />
    {/* dispenser display */}
    <path d="M16.7 14.4 H19.3" opacity="0.7" />
    {/* nozzle hanging */}
    <path d="M20 15.5 H21.5 V17" />
    {/* base platform */}
    <path d="M3 18 H21" />
    {/* water waves below */}
    <path d="M2 21 Q4 20 6 21 T10 21 T14 21 T18 21 T22 21" opacity="0.7" />
  </svg>
);

const SOLUTIONS = [
  {
    slug: "floating-infrastructure-solutions",
    title: "Floating Infrastructure Solutions",
    icon: Boxes,
    intro: "Modular, scalable floating systems for waterfront developments, marine operations and offshore access.",
    services: ["Floating Pontoons", "Floating Docks & Jetties", "Floating Platforms & Marinas", "Floating Breakwaters & Bridges"],
  },
  {
    slug: "marine-navigation-safety-solutions",
    title: "Marine Navigation & Safety Solutions",
    icon: BeaconIcon,
    intro: "Intelligent navigation and safety systems that improve visibility and reduce operational risk on the water.",
    services: ["Solar Marine Beacons", "Channel Markers & Buoys", "Hazard & Warning Lights", "Port & Waterway Safety Systems"],
  },
  {
    slug: "marine-access-berthing-solutions",
    title: "Marine Access & Berthing Solutions",
    icon: Anchor,
    intro: "Safe, reliable and efficient access for vessels, personnel and waterfront operations that protect marine assets.",
    services: ["Gangways & Boarding Bridges", "Mooring & Fender Systems", "Jetties & Docks", "Vessel Berthing Systems"],
  },
  {
    slug: "coastal-waterfront-solutions",
    title: "Coastal & Waterfront Solutions",
    icon: Waves,
    intro: "Resilient coastal and waterfront infrastructure that protects shorelines and supports long-term development.",
    services: ["Coastal & Shoreline Protection", "Waterfront Development & Estates", "Marina Development", "Seawalls & Revetments"],
  },
  {
    slug: "marine-coating-solutions",
    title: "Marine Infrastructure Coating Solutions",
    icon: Shield,
    intro: "Advanced protective coatings that extend asset life, resist corrosion and cut long-term maintenance costs.",
    services: ["Marine & Offshore Coatings", "Corrosion & Steel Protection", "Tank, Pipeline & Bridge Coatings", "Inspection & Maintenance Programs"],
  },
  {
    slug: "marine-vessels-equipment-solutions",
    title: "Marine Vessels & Equipment Solutions",
    icon: Ship,
    intro: "Specialised vessels and equipment for offshore, port, security, industrial and commercial marine operations.",
    services: ["Work, Patrol & Crew Boats", "Utility Boats, Barges & Yachts", "Marine Equipment & Accessories", "Marine Safety Equipment"],
  },
];

const STATS = [
  { icon: Building2, k: "100+", v: "Projects Delivered" },
  { icon: Users, k: "50+", v: "Happy Clients Across Africa" },
  { icon: Globe, k: "10+", v: "Countries Served" },
  { icon: Award, k: "15+", v: "Years Of Combined Experience" },
];

const INDUSTRIES = [
  { icon: Landmark, name: "Government" },
  { icon: Ship, name: "Ports" },
  { icon: Fuel, name: "Oil & Gas" },
  { icon: Waves, name: "Marine" },
  { icon: Anchor, name: "Waterways" },
  { icon: Building2, name: "Real Estate" },
  { icon: Trees, name: "Tourism" },
  { icon: Factory, name: "Industrial" },
  { icon: Zap, name: "Utilities" },
];

const WHY_CHOOSE = [
  { icon: Lightbulb, title: "Innovative Engineering", copy: "Modern infrastructure designed specifically for Africa's marine environment." },
  { icon: Wrench, title: "End-To-End Solutions", copy: "From feasibility studies and design to installation, commissioning and maintenance." },
  { icon: Globe, title: "International Standards", copy: "Delivered using globally recognised engineering principles and best practices." },
  { icon: Leaf, title: "Sustainable Development", copy: "Solutions that balance economic growth with environmental responsibility." },
  { icon: ShieldCheck, title: "Trusted Partnership", copy: "Working alongside clients to deliver long-term value and reliable infrastructure." },
  { icon: Headphones, title: "Technical Support", copy: "Dedicated engineering support before, during and after project delivery." },
];

// Accurate Africa continent outline with interactive regional markers
const REGIONS = [
  {
    id: "north",
    x: 200, y: 90,
    name: "North Africa",
    countries: ["Egypt", "Morocco", "Algeria", "Tunisia", "Libya"],
    capability: "Port navigation & coastal protection",
    tipAlign: "below",
  },
  {
    id: "west",
    x: 110, y: 218,
    name: "West Africa",
    countries: ["Nigeria", "Ghana", "Côte d'Ivoire", "Senegal", "Sierra Leone"],
    capability: "Solar beacons & waterfront development",
    tipAlign: "right",
  },
  {
    id: "east",
    x: 300, y: 210,
    name: "East Africa",
    countries: ["Kenya", "Tanzania", "Ethiopia", "Uganda", "Rwanda"],
    capability: "Inland waterways & floating infrastructure",
    tipAlign: "left",
  },
  {
    id: "central",
    x: 195, y: 275,
    name: "Central Africa",
    countries: ["DR Congo", "Cameroon", "Gabon", "Congo", "Angola"],
    capability: "Marine access systems & oil & gas terminals",
    tipAlign: "right",
  },
  {
    id: "south",
    x: 210, y: 395,
    name: "Southern Africa",
    countries: ["South Africa", "Namibia", "Mozambique", "Botswana", "Zambia"],
    capability: "Marinas & coastal resilience",
    tipAlign: "above",
  },
];

const AfricaMap = ({ className }) => {
  const [active, setActive] = useState(null);
  const activeRegion = REGIONS.find((r) => r.id === active);

  // convert svg-viewBox coords → percentage of the wrapper
  const pct = (v, axis) => `${(v / (axis === "x" ? 400 : 470)) * 100}%`;

  const tooltipTransform = (align) => {
    switch (align) {
      case "below":
        return "translate(-50%, 14px)";
      case "above":
        return "translate(-50%, calc(-100% - 14px))";
      case "left":
        return "translate(calc(-100% - 14px), -50%)";
      case "right":
      default:
        return "translate(14px, -50%)";
    }
  };

  return (
    <div className={`relative ${className}`} data-testid="africa-map">
      <svg viewBox="0 0 400 470" className="w-full h-full text-[color:var(--mia-green)]" aria-hidden="true">
        <defs>
          <radialGradient id="mia-marker-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.55" />
            <stop offset="70%" stopColor="currentColor" stopOpacity="0.08" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
          </radialGradient>
        </defs>
        {/* Africa outline */}
        <path
          d="M78 62 C115 48 165 42 195 42 C235 42 275 50 300 68 C315 78 328 92 330 110 C336 118 348 128 350 138 C350 148 342 152 336 150 C340 158 352 168 358 180 C362 190 355 196 344 192 C338 200 330 218 328 236 C326 254 316 272 302 288 C292 305 286 322 282 340 C276 362 268 380 255 400 C240 422 220 442 200 448 C182 450 168 442 158 428 C150 415 148 400 148 382 C148 362 148 342 142 324 C136 308 128 296 122 285 C115 270 118 255 128 245 C138 236 148 232 150 222 C148 212 140 208 128 210 C108 214 88 222 78 220 C65 218 55 208 58 195 C60 180 62 168 58 152 C55 138 56 122 60 108 C62 92 68 78 78 62 Z"
          fill="currentColor"
          fillOpacity="0.06"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
        {/* Madagascar */}
        <path
          d="M330 300 C338 298 344 306 342 320 C338 340 332 360 322 372 C318 366 320 350 322 336 C324 320 324 308 330 300 Z"
          fill="currentColor"
          fillOpacity="0.06"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinejoin="round"
        />
        {/* Regional markers */}
        {REGIONS.map((r) => {
          const isActive = active === r.id;
          return (
            <g
              key={r.id}
              onMouseEnter={() => setActive(r.id)}
              onMouseLeave={() => setActive(null)}
              onClick={() => setActive((a) => (a === r.id ? null : r.id))}
              className="cursor-pointer"
              data-testid={`africa-marker-${r.id}`}
            >
              {/* invisible larger hit-area */}
              <circle cx={r.x} cy={r.y} r="22" fill="transparent" />
              <circle cx={r.x} cy={r.y} r={isActive ? 26 : 22} fill="url(#mia-marker-glow)" style={{ transition: "r 200ms" }} />
              <circle cx={r.x} cy={r.y} r={isActive ? 8.5 : 6.5} fill="currentColor" opacity="0.28" style={{ transition: "r 200ms" }} />
              <circle cx={r.x} cy={r.y} r={isActive ? 3.8 : 3} fill="currentColor" style={{ transition: "r 200ms" }} />
            </g>
          );
        })}
      </svg>
      {activeRegion && (
        <div
          className="absolute z-20 pointer-events-none animate-fade-up"
          style={{
            left: pct(activeRegion.x, "x"),
            top: pct(activeRegion.y, "y"),
            transform: tooltipTransform(activeRegion.tipAlign),
          }}
          data-testid={`africa-tooltip-${activeRegion.id}`}
        >
          <div className="bg-[color:var(--mia-navy)] border border-[color:var(--mia-green)]/50 rounded-md shadow-2xl px-4 py-3 w-[210px]">
            <div className="text-[0.62rem] font-bold tracking-[0.18em] uppercase text-[color:var(--mia-green)]">
              {activeRegion.name}
            </div>
            <div className="text-[11px] text-white/85 mt-1.5 leading-snug">
              {activeRegion.countries.join(" · ")}
            </div>
            <div className="mt-2 pt-2 border-t border-white/10 text-[10px] text-white/60 italic leading-snug">
              {activeRegion.capability}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function Home() {
  return (
    <div>
      {/* HERO */}
      <section className="bg-[color:var(--mia-navy)] text-white relative" data-testid="home-hero">
        <div className="mia-container grid lg:grid-cols-2 gap-8 items-center py-14 lg:py-14 lg:py-16">
          <div className="animate-fade-up">
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight uppercase">
              Engineering Africa's<br />Marine Infrastructure<br />
              <span className="text-[color:var(--mia-green)]">For Tomorrow</span>
            </h1>
            <div className="w-14 h-1 bg-[color:var(--mia-green)] my-7" />
            <p className="text-white/85 text-base sm:text-lg leading-relaxed max-w-lg">
              Marine Infrastructure Africa is a leading marine engineering and infrastructure company
              delivering innovative solutions that improve navigation safety, waterfront development,
              coastal resilience and floating infrastructure across Africa. We partner with governments,
              ports, oil & gas companies, developers and private investors to design, supply and deliver
              world-class marine infrastructure that creates safer waterways, stronger coastal communities
              and sustainable economic growth.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link to="/request-consultation" className="mia-btn mia-btn-primary" data-testid="hero-cta-consultation">
                Request Consultation <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/solutions" className="mia-btn mia-btn-outline-white" data-testid="hero-cta-solutions">
                Explore Our Solutions <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="relative rounded-md overflow-hidden aspect-square shadow-2xl">
            <img src={HERO_IMG} alt="Marine Infrastructure Africa" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* OUR SOLUTIONS */}
      <section className="py-14 lg:py-16 bg-white" data-testid="home-solutions">
        <div className="mia-container">
          <h2 className="section-title text-2xl sm:text-3xl">OUR CORE SOLUTIONS</h2>
          <p className="text-center text-[color:var(--mia-muted)] mt-4 max-w-3xl mx-auto">
            Integrated marine infrastructure solutions for ports, inland waterways, waterfront developments,
            offshore facilities, marinas, tourism destinations and industrial marine projects across Africa.
          </p>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {SOLUTIONS.map((s) => (
              <Link
                key={s.slug}
                to={`/solutions/${s.slug}`}
                className="group bg-white border-2 border-slate-100 hover:border-[color:var(--mia-navy)] rounded-lg p-6 sm:p-7 card-lift flex flex-col"
                data-testid={`solution-${s.slug}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[color:var(--mia-grey)] text-[color:var(--mia-navy)] flex items-center justify-center shrink-0 group-hover:bg-[color:var(--mia-navy)] group-hover:text-white transition-colors">
                    <s.icon className="w-6 h-6" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display font-black text-[color:var(--mia-navy)] text-sm sm:text-base tracking-wide uppercase leading-tight">
                    {s.title}
                  </h3>
                </div>
                <p className="text-sm text-[color:var(--mia-muted)] leading-relaxed mt-5">{s.intro}</p>
                <ul className="mt-5 space-y-2 flex-1">
                  {s.services.map((svc) => (
                    <li key={svc} className="flex items-start gap-2 text-[13px] text-[color:var(--mia-text)]">
                      <ArrowRight className="w-3.5 h-3.5 text-[color:var(--mia-green)] mt-1 shrink-0" />
                      <span>{svc}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-5 border-t border-slate-100 inline-flex items-center gap-2 text-[0.72rem] font-bold tracking-[0.14em] uppercase text-[color:var(--mia-navy)] group-hover:text-[color:var(--mia-green)] transition-colors">
                  Learn More <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link to="/solutions" className="inline-flex items-center gap-2 text-sm font-bold tracking-[0.16em] uppercase text-[color:var(--mia-navy)] hover:text-[color:var(--mia-green)]" data-testid="home-view-all-solutions">
              View All Solutions <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* STATISTICS */}
      <section className="bg-[color:var(--mia-navy)] py-10 lg:py-14 relative overflow-hidden">
        <div className="mia-container grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-6 relative" data-testid="home-stats">
          {STATS.map((s) => (
            <div key={s.v} className="flex flex-col sm:flex-row items-center sm:items-center text-center sm:text-left gap-3 sm:gap-4">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white/5 text-[color:var(--mia-green)] flex items-center justify-center shrink-0">
                <s.icon className="w-6 h-6 sm:w-7 sm:h-7" strokeWidth={1.5} />
              </div>
              <div>
                <div className="font-display font-black text-white text-2xl sm:text-3xl lg:text-4xl leading-none">{s.k}</div>
                <div className="text-[0.65rem] sm:text-[0.72rem] font-bold tracking-[0.14em] uppercase text-white/70 mt-1 leading-tight">{s.v}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* INDUSTRIES WE SERVE */}
      <section className="py-14 lg:py-16 bg-white" data-testid="home-industries">
        <div className="mia-container">
          <h2 className="section-title text-2xl sm:text-3xl">INDUSTRIES WE SERVE</h2>

          <div className="mt-10 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-9 gap-4 sm:gap-6">
            {INDUSTRIES.map((i) => (
              <div key={i.name} className="text-center" data-testid={`industry-${i.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="mx-auto w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center text-[color:var(--mia-navy)]">
                  <i.icon className="w-6 h-6 sm:w-8 sm:h-8" strokeWidth={1.4} />
                </div>
                <div className="font-display font-bold text-[color:var(--mia-navy)] mt-2 sm:mt-3 text-[0.62rem] sm:text-[0.7rem] tracking-[0.12em] uppercase leading-tight">{i.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-14 lg:py-16 bg-[color:var(--mia-grey)]" data-testid="home-why">
        <div className="mia-container">
          <h2 className="font-display font-black text-3xl sm:text-4xl text-[color:var(--mia-navy)] tracking-tight">
            WHY CHOOSE<br />MARINE INFRASTRUCTURE AFRICA?
          </h2>
          <div className="w-14 h-1 bg-[color:var(--mia-navy)] mt-5" />

          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {WHY_CHOOSE.map((w) => (
              <div key={w.title} className="bg-white p-5 rounded-md card-lift" data-testid={`why-${w.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="icon-round-light">
                  <w.icon className="w-7 h-7" strokeWidth={1.5} />
                </div>
                <h3 className="font-display font-bold text-[color:var(--mia-navy)] mt-4 text-[0.78rem] tracking-[0.12em] uppercase leading-tight">{w.title}</h3>
                <p className="text-xs text-[color:var(--mia-muted)] mt-3 leading-relaxed">{w.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA with map */}
      <section className="bg-[color:var(--mia-navy)] py-14 lg:py-14 lg:py-16 text-white relative overflow-hidden">
        <div className="mia-container grid grid-cols-1 lg:grid-cols-[1fr,1.4fr,1.2fr] gap-8 items-center relative">
          <div className="text-[color:var(--mia-green)] mx-auto lg:mx-0">
            <AfricaMap className="w-40 h-40 sm:w-48 sm:h-48 lg:w-60 lg:h-60" />
          </div>
          <div className="text-center lg:text-left">
            <h2 className="font-display font-black text-2xl sm:text-3xl lg:text-5xl leading-[1.05] tracking-tight uppercase">
              Let's Build Africa's<br />Marine Future<br />
              <span className="text-[color:var(--mia-green)]">Together.</span>
            </h2>
            <p className="text-white/75 mt-4 max-w-md mx-auto lg:mx-0 text-sm sm:text-base">
              Whether you are planning a marina, floating infrastructure, waterfront development or marine
              safety project, our experienced team is ready to help transform your vision into reality.
            </p>
          </div>
          <div className="flex flex-col gap-4 w-full">
            <Link to="/request-consultation" className="bg-[color:var(--mia-green)] hover:bg-[color:var(--mia-green-dark)] text-white p-5 sm:p-6 rounded-md flex items-center justify-between gap-4 transition-colors" data-testid="cta-consultation">
              <div className="min-w-0">
                <div className="font-display font-bold text-xs sm:text-sm tracking-[0.14em] uppercase">Request Consultation</div>
                <div className="text-xs text-white/80 mt-1">Tell us about your project and our team will get back to you.</div>
              </div>
              <ArrowRight className="w-5 h-5 shrink-0" />
            </Link>
            <Link to="/contact" className="border-2 border-white/40 hover:border-white text-white p-5 sm:p-6 rounded-md flex items-center justify-between gap-4 transition-colors" data-testid="cta-proposal">
              <div className="min-w-0">
                <div className="font-display font-bold text-xs sm:text-sm tracking-[0.14em] uppercase">Contact Us</div>
                <div className="text-xs text-white/70 mt-1">Speak with our engineering team about your next project.</div>
              </div>
              <ArrowRight className="w-5 h-5 shrink-0" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
